import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post('/api/auth/forgot-password', { email, origin: window.location.origin });
            setSubmitted(true);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC] p-8">
                <div className="w-full max-w-md">
                    <div className="brutalist-card p-8 text-center" data-testid="reset-success-card">
                        <div className="w-16 h-16 rounded-full bg-green-100 border-2 border-zinc-950 flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                        </div>
                        <h1 className="text-2xl font-black tracking-tight text-zinc-950 mb-4">Check Your Email</h1>
                        <p className="text-zinc-600 mb-6">
                            If an account exists with <span className="font-semibold">{email}</span>, you will receive a password reset link shortly.
                        </p>
                        <p className="text-sm text-zinc-500 mb-8">
                            The link will expire in 1 hour. Check your spam folder if you don't see it.
                        </p>
                        <Link
                            to="/login"
                            className="brutalist-btn-secondary inline-flex items-center gap-2"
                            data-testid="back-to-login-link"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back to Sign In
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC] p-8">
            <div className="w-full max-w-md">
                <div className="mb-8">
                    <Link to="/login" className="inline-flex items-center gap-2 text-zinc-600 hover:text-zinc-950 mb-6">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Sign In
                    </Link>
                    <h1 className="text-3xl font-black tracking-tight text-zinc-950 mb-2">Forgot Password?</h1>
                    <p className="text-zinc-600">Enter your email and we'll send you a reset link.</p>
                </div>

                <div className="brutalist-card p-8" data-testid="forgot-password-card">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="p-4 bg-red-50 border-2 border-red-500 rounded-lg" data-testid="forgot-password-error">
                                <p className="text-red-700 font-medium">{error}</p>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-semibold text-zinc-700 mb-2">
                                Email Address
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="brutalist-input has-leading-icon"
                                    placeholder="you@example.com"
                                    required
                                    data-testid="forgot-password-email-input"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="brutalist-btn-primary w-full flex items-center justify-center gap-2"
                            data-testid="forgot-password-submit-button"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                'Send Reset Link'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
